import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addTeamMemberSkills,
  getTeamMemberSkills,
  removeTeamMemberSkills,
  updateTeamMemberSkillProficiency,
} from "controller/api/control-center/team-members";
import type {
  AddTeamMemberSkillItem,
  ApiErrorType,
  SkillProficiencyValue,
  TeamMemberSkillCategoryEntry,
} from "shared";
import { sentryLogger } from "utilz";

/**
 * Exported so writes that change a member's skills INDIRECTLY can drop this
 * cache — `useUpdateResource` assigns role profiles, and a role profile grants
 * skills, so that save changes this list server-side without touching any
 * skills endpoint.
 */
export const teamMemberSkillsKey = (resourceId: string) =>
  ["team-member-skills", resourceId] as const;

const skillsKey = teamMemberSkillsKey;

/**
 * Lists the team member's assigned skills, grouped by category. Cache slot
 * is per-resource so switching between members doesn't re-fetch unnecessarily.
 */
export const useTeamMemberSkills = (resourceId: string) => {
  return useQuery({
    enabled: Boolean(resourceId),
    queryKey: skillsKey(resourceId),
    queryFn: () => getTeamMemberSkills(resourceId),
    staleTime: 30_000,
    /**
     * The Skills tab is mounted/unmounted by the detail screen's tab switch, so
     * "on mount" is "every time the user opens the tab".
     *
     * `"always"` rather than `staleTime: 0` on purpose: the cached list still
     * paints instantly while the refetch runs, so the tab does not flash empty
     * — but it can never show a skill set that a role-profile change made wrong
     * seconds earlier on the Overview tab. `staleTime` is kept for the writes
     * that already invalidate this key explicitly.
     */
    refetchOnMount: "always",
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getTeamMemberSkills",
      }),
  });
};

/**
 * Assigns one or more master skills to the member. Cache is invalidated on
 * success so the list refetches with the new `resource_skill_id`s from the
 * server (we can't optimistically construct them).
 */
export const useAddTeamMemberSkills = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      resourceId: string;
      skills: AddTeamMemberSkillItem[];
    }) => addTeamMemberSkills(payload.resourceId, payload.skills),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: skillsKey(variables.resourceId),
      });
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "addTeamMemberSkills",
      }),
  });
};

/**
 * Optimistically swaps the proficiency in the cache so the chip updates
 * instantly. Rolls back on error and refetches on success/error settle for
 * the authoritative value.
 */
export const useUpdateTeamMemberSkillProficiency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      resourceId: string;
      resourceSkillId: string;
      proficiency: SkillProficiencyValue;
    }) =>
      updateTeamMemberSkillProficiency(
        payload.resourceId,
        payload.resourceSkillId,
        payload.proficiency,
      ),
    onMutate: async (payload) => {
      const key = skillsKey(payload.resourceId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous =
        queryClient.getQueryData<TeamMemberSkillCategoryEntry[]>(key);
      if (!previous) return { previous };
      const next = previous.map((cat) => ({
        ...cat,
        skills: cat.skills.map((s) =>
          s.resource_skill_id === payload.resourceSkillId
            ? { ...s, proficiency: payload.proficiency }
            : s,
        ),
      }));
      queryClient.setQueryData(key, next);
      return { previous };
    },
    onError: (error: ApiErrorType, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          skillsKey(variables.resourceId),
          context.previous,
        );
      }
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateTeamMemberSkillProficiency",
      });
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({
        queryKey: skillsKey(variables.resourceId),
      });
    },
  });
};

/**
 * Optimistically drops the removed skill(s) from the cached category list —
 * removes empty categories so the UI doesn't keep an empty section card.
 */
export const useRemoveTeamMemberSkills = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { resourceId: string; resourceSkillIds: string[] }) =>
      removeTeamMemberSkills(payload.resourceId, payload.resourceSkillIds),
    onMutate: async (payload) => {
      const key = skillsKey(payload.resourceId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous =
        queryClient.getQueryData<TeamMemberSkillCategoryEntry[]>(key);
      if (!previous) return { previous };
      const removeSet = new Set(payload.resourceSkillIds);
      const next = previous
        .map((cat) => ({
          ...cat,
          // A manual override is NOT deleted by this call — backend drops the
          // override and the skill reverts to its inherited value, keeping the
          // row. Optimistically removing it would flash the row out and pull it
          // back on the `onSettled` refetch, which reads as a failed delete.
          // Leave it in place at its stale level; the refetch corrects it.
          skills: cat.skills.filter(
            (s) =>
              !removeSet.has(s.resource_skill_id) ||
              s.is_manual_override === true,
          ),
        }))
        .filter((cat) => cat.skills.length > 0);
      queryClient.setQueryData(key, next);
      return { previous };
    },
    onError: (error: ApiErrorType, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          skillsKey(variables.resourceId),
          context.previous,
        );
      }
      sentryLogger({
        error,
        workflow: "controlCenter",
        level: "error",
        functionName: "removeTeamMemberSkills",
      });
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({
        queryKey: skillsKey(variables.resourceId),
      });
    },
  });
};
